import { NodeExtension, MarkExtension, ApplySchemaAttributes, NodeSpecOverride, CommandFunction } from '@remirror/core';
import { NodeView, EditorView } from 'prosemirror-view';
import { Node as ProsemirrorNode } from 'prosemirror-model';
import { Plugin, PluginKey } from 'prosemirror-state';
import { createRoot, Root } from 'react-dom/client';
import { LinkPopoverComponent } from './components/Popover';

// Sistema global para gestionar popovers abiertos
class LinkPopoverManager {
  private static instance: LinkPopoverManager;
  private openPopover: CustomLinkNodeView | null = null;

  static getInstance(): LinkPopoverManager {
    if (!LinkPopoverManager.instance) {
      LinkPopoverManager.instance = new LinkPopoverManager();
    }
    return LinkPopoverManager.instance;
  }

  open(popover: CustomLinkNodeView) {
    // Cerrar el popover anterior si existe
    if (this.openPopover && this.openPopover !== popover) {
      this.openPopover.hidePopover();
    }
    this.openPopover = popover;
  }

  close(popover: CustomLinkNodeView) {
    if (this.openPopover === popover) {
      this.openPopover = null;
    }
  }

  closeAll() {
    if (this.openPopover) {
      this.openPopover.hidePopover();
      this.openPopover = null;
    }
  }
}


// NodeView personalizado para el link
class CustomLinkNodeView implements NodeView {
  dom: HTMLElement;
  linkElement: HTMLAnchorElement;
  popoverContainer: HTMLElement;
  root: Root | null = null;
  private showPopover = false;
  private boundHandleOutsideClick: (e: MouseEvent) => void;
  private onEditLink?: (text: string, href: string) => void;

  constructor(
    private node: ProsemirrorNode,
    private view: EditorView,
    private getPos: () => number,
  ) {
    // Crear wrapper
    this.dom = document.createElement("div");
    this.dom.className = "custom-link-wrapper";
    this.dom.style.position = "relative";
    this.dom.style.display = "inline-block";

    // Crear link (mantener como elemento principal)
    this.linkElement = document.createElement("a");
    this.linkElement.href = node.attrs.href;
    this.linkElement.target = "_blank";
    this.linkElement.textContent = node.attrs.text || node.attrs.href;
    this.linkElement.style.textDecoration = "underline";
    this.linkElement.style.color = "#007cba";
    this.linkElement.style.cursor = "pointer";

    // Crear contenedor para popover
    this.popoverContainer = document.createElement("div");
    this.popoverContainer.style.position = "absolute";
    this.popoverContainer.style.top = "100%";
    this.popoverContainer.style.left = "0";
    this.popoverContainer.style.zIndex = "10";
    this.popoverContainer.style.display = "none";

    // Ensamblar: wrapper contiene el link y el popover
    this.dom.appendChild(this.linkElement);
    this.dom.appendChild(this.popoverContainer);

    // Event listeners
    this.linkElement.addEventListener("click", this.handleLinkClick.bind(this));
    this.dom.addEventListener("click", this.handleWrapperClick.bind(this));

    // Vincular handleOutsideClick para poder removerlo después
    this.boundHandleOutsideClick = this.handleOutsideClick.bind(this);
    document.addEventListener("click", this.boundHandleOutsideClick);
  }

  setEditCallback(callback: (text: string, href: string) => void) {
    this.onEditLink = callback;
  }

  private handleLinkClick(e: MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    this.togglePopover();
  }

  private handleWrapperClick(e: MouseEvent) {
    e.stopPropagation();
  }

  private handleOutsideClick(e: MouseEvent) {
    if (!this.dom.contains(e.target as Node)) {
      // Cerrar todos los popovers cuando se hace click fuera
      LinkPopoverManager.getInstance().closeAll();
    }
  }

  private togglePopover() {
    if (this.showPopover) {
      this.hidePopover();
    } else {
      this.showPopoverInternal();
    }
  }

  private showPopoverInternal() {
    // Registrar este popover como abierto en el manager
    LinkPopoverManager.getInstance().open(this);

    this.showPopover = true;
    this.popoverContainer.style.display = "block";

    if (this.root) this.root.unmount();
    this.root = createRoot(this.popoverContainer);

    this.root.render(
      <LinkPopoverComponent
        href={this.node.attrs.href}
        text={this.node.attrs.text || this.node.attrs.href}
        onEdit={this.handleEdit.bind(this)}
        onClose={this.hidePopover.bind(this)}
        onDelete={this.handleDelete.bind(this)}
      />
    );
  }

  hidePopover() {
    this.showPopover = false;
    this.popoverContainer.style.display = "none";

    if (this.root) {
      this.root.unmount();
      this.root = null;
    }
    this.popoverContainer.innerHTML = "";

    // Notificar al manager que se cerró
    LinkPopoverManager.getInstance().close(this);
  }

  private handleEdit(text: string, href: string) {
    if (this.onEditLink) {
      // Usar el callback para abrir el modal de edición
      this.onEditLink(text, href);
    } else {
      // Fallback: actualizar directamente el nodo
      const pos = this.getPos();
      const { state, dispatch } = this.view;

      // Actualizar el nodo
      const tr = state.tr.setNodeMarkup(pos, undefined, {
        ...this.node.attrs,
        href,
        text
      });

      dispatch(tr);
    }
    this.hidePopover();
  }

  private handleDelete() {
    const pos = this.getPos();
    const { state, dispatch } = this.view;

    // Reemplazar el nodo del enlace con texto plano
    const textNode = state.schema.text(this.node.attrs.text || this.node.attrs.href);
    const tr = state.tr.replaceWith(pos, pos + this.node.nodeSize, textNode);

    dispatch(tr);
  }

  update(node: ProsemirrorNode) {
    if (node.type !== this.node.type) return false;

    this.node = node;
    this.linkElement.href = node.attrs.href;
    this.linkElement.textContent = node.attrs.text || node.attrs.href;

    return true;
  }

  destroy() {
    if (this.root) {
      this.root.unmount();
    }
    // Remover el event listener correctamente
    document.removeEventListener("click", this.boundHandleOutsideClick);

    // Notificar al manager que este popover se destruyó
    LinkPopoverManager.getInstance().close(this);
  }
}

// Extensión para el mark de link (para compatibilidad con el código existente)
export class CustomLinkMarkExtension extends MarkExtension {
  get name() {
    return 'link';
  }

  createMarkSpec(extra: ApplySchemaAttributes, override: NodeSpecOverride) {
    return {
      attrs: {
        href: { default: '' },
        target: { default: '_blank' },
        ...extra.defaults(),
      },
      inclusive: false,
      parseDOM: [{
        tag: 'a[href]',
        getAttrs: (dom: HTMLElement) => ({
          href: dom.getAttribute('href') || '',
          target: dom.getAttribute('target') || '_blank',
          ...extra.parse(dom),
        }),
      }],
      toDOM: (mark) => {
        const { href, target } = mark.attrs;
        return ['a', { href, target, style: 'text-decoration: underline; color: #007cba;' }, 0] as any;
      },
    };
  }

  createCommands() {
    return {
      updateLink: (options: { href: string; text?: string }): CommandFunction => {
        return ({ tr, dispatch, state }) => {
          const { from, to } = state.selection;
          const mark = this.type.create({ href: options.href, target: '_blank' });

          // Aplicar el mark al texto seleccionado
          const newTr = tr.addMark(from, to, mark);

          if (dispatch) dispatch(newTr);
          return true;
        };
      },
    };
  }
}

// Extensión para el nodo de link (para el wrapper personalizado)
export class CustomLinkExtension extends NodeExtension {
  private editLinkCallback?: (text: string, href: string) => void;

  constructor(private linkOptions: { autoLink?: boolean; selectTextOnClick?: boolean } = {}) {
    super();
  }

  setEditLinkCallback(callback: (text: string, href: string) => void) {
    this.editLinkCallback = callback;
  }

  get name() {
    return 'customLink';
  }

  createNodeSpec(extra: ApplySchemaAttributes, override: NodeSpecOverride) {
    return {
      inline: true,
      group: 'inline',
      selectable: true,
      draggable: true,
      attrs: {
        href: { default: '' },
        text: { default: '' },
        ...extra.defaults(),
      },
      parseDOM: [{
        tag: 'a[href]',
        getAttrs: (dom: HTMLElement) => ({
          href: dom.getAttribute('href') || '',
          text: dom.textContent || '',
          ...extra.parse(dom),
        }),
        // Asegurar que los elementos <a> se conviertan en nodos customLink
        priority: 100,
      }],
      toDOM: (node) => {
        const { href, text } = node.attrs;

        // Retornar estructura: div wrapper > a link
        return [
          'div',
          { class: 'custom-link-wrapper', style: 'position: relative; display: inline-block;' },
          ['a', { href, target: '_blank', style: 'text-decoration: underline; color: #007cba; cursor: pointer;' }, text || href]
        ] as any;
      },
    };
  }

  createNodeViews() {
    return {
      customLink: (node, view, getPos) => {
        const nodeView = new CustomLinkNodeView(node, view, getPos!);
        if (this.editLinkCallback) {
          nodeView.setEditCallback(this.editLinkCallback);
        }
        return nodeView;
      },
    };
  }

  createPlugins() {
    return [
      new Plugin({
        key: new PluginKey('customLinkPaste'),
        props: {
          handlePaste: (view, event, slice) => {
            const { state, dispatch } = view;
            const { schema } = state;

            // Verificar si el slice contiene links
            let hasLinks = false;
            slice.content.descendants((node, pos) => {
              if (node.marks.some(mark => mark.type.name === 'link')) {
                hasLinks = true;
                return false;
              }
              return true;
            });

            if (hasLinks) {
              // Convertir links a nodos customLink
              const tr = state.tr;
              const { from, to } = state.selection;

              // Reemplazar todo el contenido pegado
              const customLinkNodes: ProsemirrorNode[] = [];
              slice.content.descendants((node, pos) => {
                if (node.marks.some(mark => mark.type.name === 'link')) {
                  const linkMark = node.marks.find(mark => mark.type.name === 'link');
                  if (linkMark) {
                    const customLinkNode = schema.nodes.customLink.create({
                      href: linkMark.attrs.href,
                      text: node.text
                    });
                    customLinkNodes.push(customLinkNode);
                  }
                } else {
                  customLinkNodes.push(node);
                }
                return true;
              });

              if (customLinkNodes.length > 0) {
                tr.replaceWith(from, to, customLinkNodes);
                dispatch(tr);
                return true;
              }
            }

            return false;
          }
        }
      })
    ];
  }

  createCommands() {
    return {
      insertLink: (options: { href: string; text?: string }): CommandFunction => {
        return ({ tr, dispatch }) => {
          const nodeType = this.type;
          const node = nodeType.create({
            href: options.href,
            text: options.text || options.href,
          });
          const transaction = tr.replaceSelectionWith(node).scrollIntoView();
          if (dispatch) dispatch(transaction);
          return true;
        };
      },
      insertCustomLink: (options: { href: string; text?: string }): CommandFunction => {
        return ({ tr, dispatch }) => {
          const nodeType = this.type;
          const node = nodeType.create({
            href: options.href,
            text: options.text || options.href,
          });
          const transaction = tr.replaceSelectionWith(node).scrollIntoView();
          if (dispatch) dispatch(transaction);
          return true;
        };
      },
    };
  }
}
